import { UIMessage, type Message } from 'ai'
import { string, array, number, object, InferType, mixed, boolean } from 'yup'
import { CorrespondenceContact } from './query-intent'


// TODO: DB. Do we need to migrate saved chat messages property to the new format?
export interface Chat extends Record<string, any> {
  id: string
  title: string
  createdAt: number
  userId: string
  path: string
  matterId: string
  accountId: string
  messages: UIMessage[]
  sharePath?: string
  type: ChatType
  fileIds?: Array<string>
  uneditable?: boolean
  promptTemplateId?: string
  selectedKnowledgeLibraryIds?: string[]
}

export const intentMap = {
  '': undefined,
  MatterQuestion: {
    intentType: { intentType: 'MatterQuestion' },
    parsed: {
      intentType: 'matterquestion',
      storeIntentForReuse: true
    }
  },
  LegalQuestion: {
    intentType: { intentType: 'LegalQuestion' },
    parsed: {
      intentType: 'legalquestion',
      storeIntentForReuse: true
    }
  },
  Email: {
    parsed: {
      functionRequests: {
        emailDraft: {
          active: true,
          to: [] as CorrespondenceContact[]
        }
      },
      intentType: 'email',
      storeIntentForReuse: true
    }
  },
  Letter: {
    parsed: {
      docTypesExtraction: [],
      requestsFullSearch: false,
      dateBoundaries: {
        fromDate: null,
        toDate: null,
        inconclusive: false
      },
      functionRequests: {
        letterDraft: {
          active: true,
          to: [] as CorrespondenceContact[]
        }
      },
      scratchpad: '',
      intentType: 'letter',
      storeIntentForReuse: true,
      translationDetails: {
        isTranslation: false
      }
    }
  },
  Document: {
    parsed: {
      intentType: 'document',
      storeIntentForReuse: true
    }
  },
  CourtDocument: {
    parsed: {
      intentType: 'courtdocument',
      storeIntentForReuse: true
    }
  },
  Clause: {
    parsed: {
      functionRequests: {
        clauseDraft: {
          active: true
        }
      },
      intentType: 'clause',
      storeIntentForReuse: true
    }
  },
  Memo: {
    parsed: {
      functionRequests: {
        memoDraft: {
          active: true
        }
      },
      intentType: 'memo',
      storeIntentForReuse: true
    }
  }
}

export const askAQuestionOptions: IntentOption[] = [
  { label: 'About This Matter', value: 'MatterQuestion' },
  { label: 'A General Legal Question', value: 'LegalQuestion' }
]
export const draftAnEmailOptions: IntentOption[] = [
  { label: 'Email', value: 'Email' },
  { label: 'Letter', value: 'Letter' }
]
export const draftADocumentOptions: IntentOption[] = [
  { label: 'Clause', value: 'Clause' },
  { label: 'Document', value: 'Document' },
  { label: 'Memo', value: 'Memo' }
]

export const promptOptions: { [key: string]: IntentOption[] } = {
  MatterQuestion: askAQuestionOptions,
  LegalQuestion: askAQuestionOptions,
  Email: draftAnEmailOptions,
  Letter: draftAnEmailOptions,
  Clause: draftADocumentOptions,
  Document: draftADocumentOptions,
  Memo: draftADocumentOptions
}

export type PrefilledIntentType = keyof typeof intentMap

export type IntentCorrespondenceContact = {
  name: string
  email?: string | undefined
  id: string
}

export const getDraftPromptTemplate = (intent: PrefilledIntentType, contacts?: IntentCorrespondenceContact[]) => {
  switch (intent) {
    case '':
    case 'MatterQuestion':
    case 'LegalQuestion':
      return ''
    case 'Email':
      return `Draft an email to ${contacts?.length ? contacts.map(contact => contact?.name || '').join(', ') + ' ' : ''}`
    case 'Letter':
      return `Draft a letter to ${contacts?.length ? contacts.map(contact => contact?.name || '').join(', ') + ' ' : ''}`
    case 'Clause':
      return `Draft a clause `
    case 'Document':
    case 'CourtDocument':
      return `Draft a `
    case 'Memo':
      return `Draft a memo `
  }
}

export type IntentOption = { label: string; value: PrefilledIntentType }

export const chatValidator = (thing: any): Chat => {
  const chat = thing as Chat

  if (
    !chat.id ||
    !chat.title ||
    !chat.createdAt ||
    !chat.userId ||
    !chat.path ||
    !chat.messages ||
    !chat.type ||
    (chat.type === ChatType.Matter && !chat.matterId)
  ) {
    throw new Error('Invalid chat')
  }

  return chat
}

export type ChatHistoryItem = {
  id: string
  path: string
  userId: string
  matterId: string
  title: string
  createdAt: number
  type: ChatType
  deleted: boolean
  isFileChat?: boolean
  isArchieDraftChat?: boolean
  badge?: {
    initials: string;
    variant: string;
  }
}

export type MatterUserChatTrackingItem = {
  matterId: string
  userId: string
}

export type ChatHistoryItemWithEntityDetails = ChatHistoryItem & {
  userName?: string
  userEmail?: string
  matterNumber?: string
  matterTitle?: string
  matterDescription?: string
}

export type MessageFeedback = {
  chatId: string
  messageId: string
  positive: boolean
  showFeedbackPanel: boolean
  feedbackPanelLoading?: boolean
  feedbackPanelSubmitted?: boolean
  userInput?: string
  matterId?: string
}

export type ServerActionResult<Result> = Promise<
  | Result
  | {
    error: string
  }
>

export type ActionSource = 'ActionIcons' | 'CodeBlockAction' | 'Canvas'
export type ActionType = 'Clipboard' | 'Communicate' | 'Memo' | 'Outlook' | 'Word';

export enum ChatType {
  Web = 'web',
  Matter = 'matter'
}

const chatMessageTelemetryChunkEvalSchema = object({
  isASnippetUseful: boolean().required(),
  parsedResult: mixed().required(),
  chunkIds: array().of(array().of(string().required()).required()),
  error: string().optional(),
  responseTexts: array().of(string().required()).optional().default(undefined),
})

const preferDateRangeSchema = object({
  normalizedScoreMinTs: number(),
  rangeStartTs: number(),
  rangeEndTs: number(),
  normalizedScoreMaxTs: number(),
  preferDatesDistantFromRange: boolean(),
  orderByRelevanceAndDateModifiedWeighted: boolean(),
  relevanceScoreWeight: number(),
  dateModifiedScoreWeight: number()
})

const chunksEvalSchema = object({
  lowestRanked: chatMessageTelemetryChunkEvalSchema.optional().default(undefined),
  twentyFivePercentile: chatMessageTelemetryChunkEvalSchema.optional().default(undefined),
  fiftyPercentile: chatMessageTelemetryChunkEvalSchema.optional().default(undefined),
  seventyFivePercentile: chatMessageTelemetryChunkEvalSchema.optional().default(undefined),
})

export const chatMessageTelemetrySchema = object({
  messageId: string().required(),
  chatId: string().required(),
  userQuery: string(),
  queryIntent: object().optional(),
  queryIntentPrompt: string(),
  contextType: string(),
  contextTypeMultiRetrieval: array().of(string().optional().nullable()).optional(),
  contextIds: array().of(mixed().required()).optional(),
  contextIdsMultiRetrieval: array().of(array().of(mixed().required()).optional().nullable()).optional(),
  contextIdsMultiKLRetrieval: array().of(array().of(mixed().required()).optional().nullable()).optional(),
  prompt: string(),
  promptMultiRetrieval: array().of(string().optional().nullable()).optional(),
  completion: string(),
  completionMultiRetrieval: array().of(string().optional().nullable()).optional(),
  queryIntentMs: number(),
  contextMs: number(),
  contextMsMultiRetrieval: array().of(number()).optional(),
  vectorDbSearchMs: number().optional(),
  vectorDbSearchMsMulti: array().of(number()).optional(),
  vectorDbSearchMsMultiRetrieval: array().of(array().of(array().of(number())).optional().nullable()).optional(),
  rerankMs: number().optional(),
  rerankMsMulti: array().of(number()).optional(),
  rerankMsMultiRetrieval: array().of(array().of(number()).optional().nullable()).optional(),
  mmrProcMsMultiRetrieval: array().of(array().of(number()).optional().nullable()).optional(),
  mmrFetchMsMultiRetrieval: array().of(array().of(number()).optional().nullable()).optional(),
  vectorTextsFetchMsMultiRetrieval: array().of(array().of(number()).optional().nullable()).optional(),
  requestMs: number(),
  augmentChunksMs: number().optional(),
  additionalHighScoringChunksMs: number().optional(),
  queryIntentModel: string().optional(),
  queryIntentType: string().optional(),
  completionModel: string().optional(),
  embedsModel: string().optional(),
  startTs: number().optional(),
  fileIds: array().of(string().required()).optional(),
  excludedRecordTypes: array().of(string().required()).optional(),
  contextQueryDetails: string().optional(),
  contextQueryDetailsMulti: array().of(string()).optional(),
  contextQueryDetailsMultiRetrieval: array().of(
    array().of(
      array().of(string())
    ).optional().nullable()
  ).optional(),
  contextEsQueryDetails: string().optional(),
  contextEsQueryDetailsMulti: array().of(string()).optional(),
  contextEsQueryDetailsMultiRetrieval: array().of(
    array().of(
      array().of(string())
    )
  ).optional(),
  esSearchMs: number().optional(),
  esSearchMsMulti: array().of(number()).optional(),
  esSearchMsMultiRetrieval: array().of(array().of(array().of(number()))).optional(),
  esSearchError: string().optional(),
  esSearchErrorMulti: array().of(string().nullable()).optional(),
  esSearchErrorMultiRetrieval: array().of(array().of(array().of(string().nullable()))).optional(),
  dateFilter: object({
    dateFrom: number().optional(),
    dateTo: number().optional()
  }).optional(),
  dateFilterMultiRetrieval: array().of(object({
    dateFrom: number().optional(),
    dateTo: number().optional()
  }).optional().nullable()).optional(),
  preferDateRange: preferDateRangeSchema.optional().nullable(),
  preferDateRangeMultiRetrieval: array().of(preferDateRangeSchema.optional().nullable()).optional(),
  chunksEval: array().of(chunksEvalSchema).optional(),
  chunksEvalMultiRetrieval: array().of(array().of(chunksEvalSchema.optional().nullable())).optional(),
  queryTriagePrompt: string().optional(),
  klQueryTriagePrompt: string().optional(),
  promptTemplateId: string().optional(),
  timezone: string().optional(),
  availableKLIds: array().of(string()).optional(),
  userFilteredKLIds: array().of(string()).optional(),
  klVectorTextsFetchMs: number().optional(),
  klContextQueryDetailsMultiRetrieval: array().of(
    array().of(string().optional().nullable())
  ).optional(),
  klVectorDbSearchMsMultiRetrieval: array().of(array().of(number().nullable().optional())).optional(),
  klRerankMs: number().optional(),
})

export type ChatMessageTelemetry = InferType<typeof chatMessageTelemetrySchema>

export type FileMetadata = {
  dateCreated: number
  dateModified: number
  fileExtension: string
  fileId: string
  fileName: string
  fileVersionId: string
  docType: string
  additionalDocType: string
  created_at?: string
  updated_at?: string
  emailFrom?: string,
  emailTo?: string,
  emailAttachments?: string
}

export const DEFAULT_DATE_MODIFIED_SCORE_WEIGHT = 0.3
export const DEFAULT_RELEVANCE_SCORE_WEIGHT = 0.7
